import type {JSX, MouseEvent, ReactNode} from 'react'
import React, {useState, useEffect} from 'react'
import Image from 'next/image'
import {clsx} from 'clsx'
import {loadImageFromKontentAI} from '@/image-loader'
import {Nav} from '../../navigation/index'
import styles from '../../navigation/navigation.module.css'

interface NavItem {
	id: string
	title: string
}

interface OnPageNavProps {
	className?: string
	navItems?: NavItem[]
	children?: ReactNode
	logoSrc?: string
	logoAlt?: string | null | undefined
	logoUrl?: string
	logoClick?: (e: MouseEvent<HTMLAnchorElement>) => void
	brandColor?: string
	locale?: string
}

export const OnPageNav = ({
	className = '',
	navItems = [],
	children = null,
	logoSrc,
	logoAlt,
	logoUrl,
	logoClick,
	brandColor = '',
	locale = '',
}: OnPageNavProps): JSX.Element => {
	const [active, setActive] = useState<string | null>(null)
	const [mobileMenuVisible, setMobileMenuVisible] = useState(false)

	const toggleNav = (): void => {
		setMobileMenuVisible((prev) => !prev)
	}

	const getActiveLinkTitle = (): string => {
		return active
			? (navItems.find((n) => n.id === active)?.title ?? '')
			: ''
	}

	useEffect(() => {
		if (navItems.length === 0) return

		const navHeight =
			document.querySelector<HTMLDivElement>('#sticky-nav')
				?.offsetHeight || 0

		const handleNavLinkClick = (e: Event): void => {
			e.preventDefault() // Prevent default behavior immediately

			const target = e.currentTarget as HTMLAnchorElement | null
			if (!target) return
			const hrefAttribute = target.getAttribute('href')
			if (hrefAttribute) {
				const isAnchorLink = hrefAttribute.startsWith('#')
				const isSeparatePageLink = hrefAttribute.startsWith('/')

				if (isAnchorLink) {
					// Prevent default behavior for anchor links to avoid jumping to the section
					e.preventDefault()
				}

				const activeLink = document.querySelector('#sticky-nav a[href]')
				if (activeLink) {
					if (styles.active) {
						activeLink.classList.remove(styles.active)
					}
				}

				const sectionId = isAnchorLink
					? hrefAttribute.substring(1)
					: null

				const section =
					isAnchorLink && sectionId
						? document.getElementById(sectionId)
						: null

				if (section) {
					setActive(sectionId)
					window.scroll({
						behavior: 'smooth',
						top: section.offsetTop - (navHeight || 0) + 1,
					})
				}

				if (isSeparatePageLink) {
					window.location.href = hrefAttribute
				}

				toggleNav()
			}
		}

		document
			.querySelectorAll(
				'#sticky-nav a[href^="#"], #sticky-nav a[href^="/"]'
			)
			.forEach((link) => {
				link.addEventListener('click', handleNavLinkClick)
			})

		const onPageScrollLinks = navItems
			.filter((item) => item.id.startsWith('#'))
			.map((i) => i.id)
			.join(',')

		const separatePageLinks = navItems
			.filter((item) => item.id.startsWith('/'))
			.map((i) => `[href="${i.id}"]`)
			.join(',')

		const allSectionsAndLinks = [onPageScrollLinks, separatePageLinks]
			.filter(Boolean)
			.join(',')

		if (allSectionsAndLinks) {
			const sectionsAndLinks =
				document.querySelectorAll(allSectionsAndLinks)
			const sectionDetails = [...sectionsAndLinks].map((s) => ({
				sectionHeight: (s as HTMLElement).offsetHeight,
				sectionId: s.getAttribute('id'),
				sectionTop:
					s.getBoundingClientRect().top +
					window.pageYOffset -
					navHeight,
			}))

			const navHighlighter = (): void => {
				const scrollY = window.pageYOffset
				const offSet = 1.25
				sectionDetails.forEach((current) => {
					if (scrollY >= current.sectionTop - offSet * navHeight) {
						setActive(current.sectionId)
					}
				})
			}

			window.addEventListener('scroll', navHighlighter, {passive: true})

			return () => {
				window.removeEventListener('scroll', navHighlighter)
			}
		}
	}, [navItems])

	useEffect(() => {
		const closeMobileMenu = (): void => {
			setMobileMenuVisible(mobileMenuVisible && false)
		}

		window.addEventListener('resize', closeMobileMenu)
		return () => {
			window.removeEventListener('resize', closeMobileMenu)
		}
	}, [mobileMenuVisible])

	useEffect(() => {
		setActive(null)
	}, [])

	let renderedLogoUrl: string | undefined

	if (locale) {
		renderedLogoUrl = logoUrl === '#' ? '' : `/${locale}${logoUrl}`
	} else {
		renderedLogoUrl = logoUrl === '#' ? '' : logoUrl
	}

	return (
		<>
			<div className="md:flex md:items-center">
				<div className="md:flex md:items-center">
					<div className="flex shrink-0 justify-between py-2 md:mr-2 md:p-0">
						{logoSrc ? (
							<a
								className="shrink-0 md:inline "
								href={renderedLogoUrl}
								onClick={logoClick}
							>
								<div className="h-8 w-auto max-w-[200px] overflow-hidden">
									<Image
										alt={logoAlt || 'Logo'}
										className="h-full w-auto object-contain"
										height={200}
										loader={loadImageFromKontentAI}
										src={logoSrc}
										width={200}
									/>
								</div>
							</a>
						) : null}
						<div className="flex items-center md:invisible md:hidden">
							<div className="mx-2 text-sm">
								{getActiveLinkTitle()}
							</div>
							<button
								aria-expanded={mobileMenuVisible}
								onClick={toggleNav}
								type="button"
							>
								<div className="flex items-center">
									<svg
										className={clsx(
											'shrink-0',
											'stroke-current',
											styles.onPageNavStyles,
											mobileMenuVisible
												? 'rotate-180'
												: ''
										)}
										height="20"
										viewBox="0 0 17.1 10.7"
										width="20"
									>
										<path
											d="M1.9,1.9l6.6,6.2l6.7-6.2"
											fill="none"
											strokeLinecap="round"
											strokeWidth="4"
										/>
									</svg>
								</div>
							</button>
						</div>
					</div>
					<div
						className={clsx(
							mobileMenuVisible
								? ''
								: 'invisible hidden md:visible md:!flex'
						)}
					>
						<Nav
							brandColor={brandColor}
							className={clsx('navigation flex-1', className)}
							label="Secondary Navigation"
						>
							{navItems.map((item) => (
								<li
									key={item.id}
									{...({selected: item.id === active} as {
										selected?: boolean
									})}
								>
									<a
										href={
											item.id.startsWith('/')
												? `/${locale}${item.id}`
												: `#${item.id.toLowerCase()}`
										}
										target="_self"
									>
										{item.title}
									</a>
								</li>
							))}
						</Nav>
					</div>
				</div>
			</div>
			<div
				className={clsx(
					mobileMenuVisible
						? ''
						: 'invisible hidden md:visible md:!flex'
				)}
			>
				{children}
			</div>
		</>
	)
}
